import React, { useState, useEffect, lazy } from "react";
import { useParams, useNavigate } from "react-router-dom";

import useProjects from "@datahooks/useProjects";

import AuthenticatedLayout from "../layouts/AuthenticatedLayout";

import AvatarLogo from "@components/general/AvatarLogo";
import TextPlaceholder from "@components/general/TextPlaceholder";
import { Tabs, TabsHeader, TabsBody, Tab, TabPanel } from "@material-tailwind/react";
import { TestableText } from "@components/general/TestableComponents";
import { Select, SelectItem, Card } from "@tremor/react";

import { featureDetails, getReleaseWithEndDate } from "@datahooks/metadata/DataUtils";
import { CARD_TYPES, ErrorCard } from "@components/cards/ErrorCard";
import { TestEventActionsProvider } from '@datahooks/events/useTestEventActions';

import { ProjectProvider } from "@datahooks/ProjectContext";
import { ReleaseProvider } from "@datahooks/ReleaseContext";
import SuspenseWithLoading from "../components/cards/shared/SuspenseWithLoading";
import ProjectNotFound from "@components/general/ProjectNotFound";
import LoadingIcon from "@components/navigation/LoadingIcon";

// Use React.lazy to lazily load these components when required
const OverviewTabPanel = lazy(() =>
  import("../components/cards/overview/OverviewTabPanel")
);
const TestTypeTabPanel = lazy(() =>
  import("../components/cards/TestTypeTabPanel")
);

export default function Project() {
  const [activeTab, setActiveTab] = useState("overview");
  const [releaseId, setReleaseId] = useState("");
  const [project, setProject] = useState(null);
  const [projectNotFound, setProjectNotFound] = useState(false);

  const { projectId, releaseId: urlReleaseId, tabId: urlTabId } = useParams();
  const navigate = useNavigate();
  const { projects, isLoading: isLoadingProjects } = useProjects();

  const updateUrlWithReleaseAndTab = (releaseId, tabId = null) => {
    if (tabId && tabId !== "overview") {
      navigate(`/projects/${projectId}/releases/${releaseId}/tabs/${tabId}`, { replace: true });
    } else {
      navigate(`/projects/${projectId}/releases/${releaseId}`, { replace: true });
    }
  };

  useEffect(() => {
    if (!isLoadingProjects) {
      const newProject = projects.find(
        (p) => p.projectId.toString() === projectId
      );

      if (newProject) {
        setProjectNotFound(false);
        setProject(newProject);
        
        // If there's a releaseId in the URL, use it; otherwise use the first release
        if (urlReleaseId && newProject.releases.some(r => r.release_id.toString() === urlReleaseId)) {
          setReleaseId(urlReleaseId);
        } else if (newProject.releases.length > 0) {
          const defaultReleaseId = newProject.releases[0].release_id.toString();
          setReleaseId(defaultReleaseId);
          
          // Update URL to include the default release if no release was specified
          if (!urlReleaseId) {
            updateUrlWithReleaseAndTab(defaultReleaseId, urlTabId);
          }
        } else {
          console.log("No releases found");
          setReleaseId("");
        }
      } else {
        setProjectNotFound(true);
      }
    }
  }, [projects, projectId, urlReleaseId, urlTabId, navigate, isLoadingProjects]);

  // Handle tab changes from URL
  useEffect(() => {
    if (urlTabId && project) {
      // Check if the tab exists for this project
      const validTabs = ["overview", ...project.testTypeIds.map(id => id.toString())];
      if (validTabs.includes(urlTabId)) {
        setActiveTab(urlTabId);
      }
    }
  }, [urlTabId, project]);

  const changeTab = (tabName) => {
    const newTab = tabName.toString();
    setActiveTab(newTab);
    
    // Update URL to include the tab
    if (releaseId) {
      updateUrlWithReleaseAndTab(releaseId, newTab);
    }
    
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const doChangeRelease = (newVal) => {
    const newReleaseId = newVal.toString();
    setReleaseId(newReleaseId);
    
    // Update the URL to reflect the new release, preserving the current tab
    updateUrlWithReleaseAndTab(newReleaseId, activeTab);
  };

  let tabHeaders = [];
  let tabPanels = [];
  const release = getReleaseWithEndDate(project?.releases || [], releaseId);

  if (project && releaseId) {
    tabHeaders = [
      <Tab
        key="overview"
        value="overview"
        className="z-0"
        onClick={() => {
          changeTab("overview");
        }}
      >
        Overview
      </Tab>,
      ...project.testTypeIds.map((testTypeId) => (
        <Tab
          key={testTypeId}
          value={testTypeId.toString()}
          className="z-0"
          onClick={() => {
            changeTab(testTypeId.toString());
          }}
        >
          {featureDetails[testTypeId].title || `Test Type ${testTypeId}`}
        </Tab>
      )),
    ];

    tabPanels = [
      <TabPanel key="overview" value="overview">
        <SuspenseWithLoading>
          <OverviewTabPanel
            releaseId={releaseId}
            title={project?.title}
            intro={project?.intro_text}
            description_image_location={project?.description_image_location}
            description_image_title={project?.description_image_title}
            releaseTitle={release?.presentation_name}
            releaseStatus={release?.release_status}
            releasedIntoTest={project?.released_into_test}
            releaseStartDate={release?.creation_date}
            releaseLastDate={release?.last_update}
            targetToLive={project?.target_to_live}
            projectId={project?.projectId}
          />
        </SuspenseWithLoading>
      </TabPanel>,
      ...project.testTypeIds.map((testTypeId) => (
        <TabPanel key={testTypeId} value={testTypeId.toString()}>
          <SuspenseWithLoading>
            <TestTypeTabPanel testTypeId={testTypeId} key={`${releaseId}_${testTypeId}`} isActive={activeTab === testTypeId.toString()} />
          </SuspenseWithLoading>
        </TabPanel>
      )),
    ];
  }

  if (projectNotFound) {
    return (
      <AuthenticatedLayout>
        <ProjectNotFound />
      </AuthenticatedLayout>
    )
  }

  if (isLoadingProjects) {
    return (
      <AuthenticatedLayout>
        <div className="flex justify-center items-center h-full">
          <LoadingIcon showText={true} />
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <ProjectProvider projectId={Number(projectId)} doChangeTab={changeTab}>
        <div data-testid="project-main" className="mt-1 p-3 sm:p-5 bg-gray-50 dark:bg-dark-tremor-background-muted rounded-lg border dark:border-dark-tremor-border  mx-auto">
          <div className="grid grid-cols-[auto_1fr_auto] items-center border-b-2 dark:border-dark-tremor-border p-3 mb-2">
            {project?.title ? (
              <>
                <AvatarLogo
                  src={project.logo_location}
                  alt={`${project.short_name || 'Project'}`}
                  size="lg"
                  displayType="logo"
                  initialsCount={3}
                />
                <TestableText
                  dataTestId="project-title"
                  variant="h2"
                  className="ml-2 md:ml-5 md:mr-5 mr-2 text-blue-500 "
                >
                  {project.title}
                </TestableText>
              </>
            ) : (
              <TextPlaceholder lines={1} width="w-1/2" />
            )}
            <div>
              {project && project.releases.length > 0 ? (
                <Select
                  className="mt-Select-button z-22"
                  label="Build"
                  value={releaseId}
                  onChange={(newVal) => doChangeRelease(newVal)}
                >
                  {project.releases.map((release) => (
                    <SelectItem
                      className="mt-Select-option w-full"
                      key={release.version_id}
                      value={release.release_id.toString()}
                    >
                      {release.presentation_name}
                    </SelectItem>
                  ))}
                </Select>
              ) : (
                <TextPlaceholder lines={1} width="w-1/2" />
              )}
            </div>
          </div>

          {project?.releases?.length === 0 ? (
            <Card>
              <ErrorCard cardType={CARD_TYPES.EMPTY} />
              <TestableText dataTestId="project-main-no-releases-title"  variant="h3" className="w-full mt-5 text-center">
                No releases found.
              </TestableText>
              <TestableText dataTestId="project-main-no-release-detail" variant="paragraph" className="w-full text-center">
                This could be because the project is new or because no releases
                have been made in the past 30 days.
              </TestableText>
            </Card>
          ) : (
            <ReleaseProvider release={release}>
              <TestEventActionsProvider>
                <Tabs
                  key={`tab-${activeTab}`} // Force re-render on tab change
                  data-testid="project-tabs"
                  className="dark:bg-dark-tremor-background-muted"
                  value={activeTab}
                >
                  <TabsHeader
                    data-testid="project-tabs-header"
                    className="dark:bg-dark-tremor-background-muted"
                    indicatorProps={{
                      className: "dark:bg-blue-500",
                    }}
                  >
                    {tabHeaders}
                  </TabsHeader>
                  <TabsBody data-testid="project-tabs-body">{tabPanels}</TabsBody>
                </Tabs>
              </TestEventActionsProvider>
            </ReleaseProvider>
          )}
        </div>
      </ProjectProvider>
    </AuthenticatedLayout>
  );
}